import React, { useEffect, useState } from 'react';

import { API_URL } from '../../services/api';
import { AspectRatio, ProjectRender } from '../../types/editor';
import { ProgressEvent } from '../../types/progress';
import { ALL_ASPECTS } from './editorState';

/* Proportioned rectangle icons for each aspect ratio. The aspect button
 * is the canonical "switch preview to this aspect" affordance — green
 * outline + ✓ when a render exists for it. Per-aspect Render button
 * lives under each tile. A right-click menu offers Render Timeline /
 * Delete Render / Download Video. A "Render All" button sits to the
 * right of the rail. */

interface AspectRailProps {
  projectId: number | null;
  /** Active aspect (drives preview canvas + the visible "selected" state). */
  currentAspect: AspectRatio;
  renders: ProjectRender[];
  progressByAspect: Record<AspectRatio, ProgressEvent | null>;
  hasClips: boolean;
  /** Switch the preview / framing-controls to a different aspect. */
  onSelectAspect: (aspect: AspectRatio) => void;
  /** Kick off renders for these aspects. */
  onRender: (aspects: AspectRatio[]) => void;
  /** Remove a render (file + row). */
  onDeleteRender: (aspect: AspectRatio) => void;
}

/* Visual proportions for each aspect tile — height is the same across
 * all four, width derived from the ratio. Keeps the rail tidy and
 * comparable at a glance. */
const ASPECT_RECT: Record<AspectRatio, { w: number; h: number }> = {
  '9:16': { w: 36, h: 64 },
  '1:1':  { w: 56, h: 56 },
  '16:9': { w: 80, h: 45 },
  '4:5':  { w: 44, h: 55 },
};

type MenuState = { aspect: AspectRatio; x: number; y: number } | null;

export default function AspectRail({
  projectId, currentAspect, renders, progressByAspect, hasClips,
  onSelectAspect, onRender, onDeleteRender,
}: AspectRailProps) {
  const [menu, setMenu] = useState<MenuState>(null);

  // Close the right-click menu on any outside click or Esc.
  useEffect(() => {
    if (!menu) return undefined;
    const close = (): void => setMenu(null);
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const rendersByAspect = new Map(renders.map((r) => [r.aspect, r] as const));

  function inFlight(a: AspectRatio): boolean {
    const live = progressByAspect[a]?.phase;
    const row = rendersByAspect.get(a)?.status;
    const phase = live || row;
    return phase === 'queued' || phase === 'rendering' || phase === 'preparing';
  }

  function isDone(a: AspectRatio): boolean {
    const r = rendersByAspect.get(a);
    return !!(r && r.status === 'done' && r.file_url);
  }

  function downloadHref(a: AspectRatio): string {
    return `${API_URL}/editor/projects/${projectId}/download/?aspect=${encodeURIComponent(a)}`;
  }

  function openMenu(e: React.MouseEvent, aspect: AspectRatio): void {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ aspect, x: e.clientX, y: e.clientY });
  }

  return (
    <section className="aspect-rail">
      <header className="aspect-rail-head">
        <span className="section-label">Aspect ratios</span>
        <button
          type="button"
          className="secondary btn--sm"
          onClick={() => onRender(ALL_ASPECTS)}
          disabled={!projectId || !hasClips}
          title="Render every aspect at once"
        >
          Render all
        </button>
      </header>

      <ul className="aspect-rail-list">
        {ALL_ASPECTS.map((a) => {
          const rect = ASPECT_RECT[a];
          const done = isDone(a);
          const live = progressByAspect[a];
          const phase = live?.phase || rendersByAspect.get(a)?.status || 'idle';
          const busy = inFlight(a);
          const isCurrent = currentAspect === a;
          const pct = busy && live?.percent ? Math.round(live.percent) : null;

          return (
            <li key={a}>
              <div
                className={[
                  'aspect-tile',
                  isCurrent ? 'is-current' : '',
                  done ? 'is-done' : '',
                  busy ? 'is-busy' : '',
                  phase === 'error' ? 'is-error' : '',
                ].filter(Boolean).join(' ')}
              >
                <button
                  type="button"
                  className="aspect-tile-button"
                  onClick={() => onSelectAspect(a)}
                  onContextMenu={(e) => openMenu(e, a)}
                  aria-pressed={isCurrent}
                  title={`Preview ${a}`}
                >
                  {/* Fixed-height envelope so the rect (whose height
                      varies per aspect) is vertically centered in a
                      uniform area — keeps the label baseline lined up
                      across all four tiles. */}
                  <span className="aspect-tile-icon-slot" aria-hidden>
                    <span
                      className="aspect-tile-rect"
                      style={{ width: rect.w, height: rect.h }}
                    >
                      {done && <span className="aspect-tile-check" aria-hidden>✓</span>}
                    </span>
                  </span>
                  <span className="aspect-tile-label">{a}</span>
                </button>
                <button
                  type="button"
                  className="ghost btn--sm aspect-tile-render"
                  onClick={() => onRender([a])}
                  disabled={!projectId || !hasClips || busy}
                >
                  {busy ? (pct !== null ? `${pct}%` : 'Rendering…') : (done ? 'Re-render' : 'Render')}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {menu && (
        <ul
          className="aspect-menu"
          style={{ top: menu.y, left: menu.x }}
          role="menu"
          onClick={(e) => e.stopPropagation()}
        >
          <li>
            <button
              type="button"
              onClick={() => { onRender([menu.aspect]); setMenu(null); }}
              disabled={!hasClips}
            >
              Render timeline
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => { onDeleteRender(menu.aspect); setMenu(null); }}
              disabled={!isDone(menu.aspect) && !rendersByAspect.has(menu.aspect)}
            >
              Delete render
            </button>
          </li>
          <li>
            <a
              href={downloadHref(menu.aspect)}
              download
              onClick={() => setMenu(null)}
              className={isDone(menu.aspect) ? '' : 'is-disabled'}
              aria-disabled={!isDone(menu.aspect)}
            >
              Download video
            </a>
          </li>
        </ul>
      )}
    </section>
  );
}
